import { AsyncButton, Button } from '@components/button';
import { Form, FormTextInput } from '@components/form';
import { FormTagsInput } from '@components/form/FormInputs/FormTagsInput';
import { useEditDatasetMutation } from '@generated/graphql';
import { Stack } from '@mui/material';
import { useNotifications } from '@notifications/Notifications';
import * as yup from 'yup';

interface DatasetsDetailsEditModalProps {
  onClose: () => void;
  detailName: string;
  detailDescription: string;
  detailTags: string[];
  datasetId: string;
  workspaceId: string;
  refetchDatasets: VoidFunction;
}

const validationSchema = yup.object({
  name: yup
    .string()
    .trim()
    .max(128, 'Must be less than 128 characters')
    .required('Name is required'),
});

export const DatasetsDetailsEditModal = ({
  onClose,
  detailName,
  detailDescription,
  detailTags,
  datasetId,
  workspaceId,
  refetchDatasets,
}: DatasetsDetailsEditModalProps) => {
  const { createAsyncNotification } = useNotifications();
  const [editDataset] = useEditDatasetMutation();
  const handleEditDatasetDetail = createAsyncNotification(
    'Successfully updated description.',
    async ({ description, name, tags }: { description: string; name: string; tags: string[] }) => {
      await editDataset({ variables: { datasetId, description, name, tags, workspaceId } });
      if (refetchDatasets) {
        void refetchDatasets();
      }
      onClose();
    },
  );
  return (
    <Form
      enableReinitialize
      onSubmit={handleEditDatasetDetail}
      initialValues={{
        name: detailName,
        description: detailDescription || '',
        tags: detailTags || [],
      }}
      validationSchema={validationSchema}
    >
      {({ handleSubmit }) => (
        <Stack>
          <FormTextInput label="Name" name="name" />
          <FormTextInput label="Description" name="description" multiline rows={3} />
          <FormTagsInput name="tags" />
          <Stack gap={4}>
            <AsyncButton
              fullWidth
              onClick={handleSubmit}
              data-cy="Datasets-Details-Edit-Modal-Save-Button"
            >
              Save
            </AsyncButton>
            <Button
              fullWidth
              variant="secondary"
              onClick={onClose}
              data-cy="Datasets-Details-Edit-Modal-Cancel-Button"
            >
              Cancel
            </Button>
          </Stack>
        </Stack>
      )}
    </Form>
  );
};
